// src/pages/OwnerDashboard.jsx
import { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy } from "firebase/firestore";
import { db, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";

export default function OwnerDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [cats, setCats] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("listings");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      if (!u) { navigate("/login"); return; }
      setUser(u);
    });
    return unsub;
  }, [navigate]);

  useEffect(() => {
    if (!user) return;

    // Load owner's cats
    const catsQ = query(
      collection(db, "cats"),
      where("ownerPhone", "==", user.phoneNumber || user.email),
      orderBy("createdAt", "desc")
    );
    const unsubCats = onSnapshot(catsQ, snap => {
      setCats(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    // Load applications for owner's cats
    const appsQ = query(
      collection(db, "applications"),
      where("ownerPhone", "==", user.phoneNumber || user.email),
      orderBy("createdAt", "desc")
    );
    const unsubApps = onSnapshot(appsQ, snap => {
      setApplications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => { unsubCats(); unsubApps(); };
  }, [user]);

  async function markAdopted(catId) {
    if (!window.confirm("Mark this cat as adopted? It will be removed from the browse listing.")) return;
    await updateDoc(doc(db, "cats", catId), { status: "adopted" });
  }

  async function toggleAvailable(catId, current) {
    await updateDoc(doc(db, "cats", catId), {
      status: current === "available" ? "paused" : "available"
    });
  }

  const pendingApps = applications.filter(a => a.status === "pending").length;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>My Dashboard</h1>
            <p style={styles.sub}>Manage your listings and adoption requests</p>
          </div>
          <button onClick={()=>navigate("/submit")} className="btn-primary">+ List a Cat</button>
        </div>

        {/* Stats */}
        <div style={styles.statsRow}>
          <StatCard icon="🐱" label="Active Listings" value={cats.filter(c=>c.status==="available").length} color="var(--rust)"/>
          <StatCard icon="📋" label="Pending Applications" value={pendingApps} color="#e07b3f" alert={pendingApps>0}/>
          <StatCard icon="❤️" label="Successfully Adopted" value={cats.filter(c=>c.status==="adopted").length} color="#4caf80"/>
          <StatCard icon="📦" label="Total Listings" value={cats.length} color="var(--muted)"/>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          {[["listings","My Cats"],["applications","Applications" + (pendingApps>0?` (${pendingApps})`:"")]].map(([tab,label])=>(
            <button key={tab} style={{...styles.tab,...(activeTab===tab?styles.tabActive:{})}} onClick={()=>setActiveTab(tab)}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{padding:60,textAlign:"center"}}><div className="spinner"/></div>
        ) : activeTab === "listings" ? (
          <div style={styles.listGrid}>
            {cats.length === 0 ? (
              <div style={styles.empty}>
                <p style={{fontSize:40}}>🐱</p>
                <p style={{marginTop:12,fontSize:16,color:"var(--muted)"}}>No listings yet. <button onClick={()=>navigate("/submit")} style={{color:"var(--rust)",background:"none",border:"none",cursor:"pointer",fontSize:16}}>List your first cat →</button></p>
              </div>
            ) : cats.map(cat => (
              <CatListingCard key={cat.id} cat={cat} onMarkAdopted={markAdopted} onToggle={toggleAvailable}/>
            ))}
          </div>
        ) : (
          <div style={styles.appList}>
            {applications.length === 0 ? (
              <div style={styles.empty}>
                <p style={{fontSize:40}}>📭</p>
                <p style={{marginTop:12,fontSize:16,color:"var(--muted)"}}>No applications yet</p>
              </div>
            ) : applications.map(app => (
              <ApplicationCard key={app.id} app={app}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color, alert }) {
  return (
    <div style={{...styles.statCard, ...(alert?{border:`2px solid ${color}`}:{})}}>
      <span style={{fontSize:28}}>{icon}</span>
      <p style={{fontSize:32,fontWeight:700,color,fontFamily:"'Playfair Display',serif"}}>{value}</p>
      <p style={{fontSize:13,color:"var(--muted)"}}>{label}</p>
    </div>
  );
}

function CatListingCard({ cat, onMarkAdopted, onToggle }) {
  const statusColors = { available:"#e8f5ee", adopted:"#f5e8e8", paused:"#fff3e0" };
  const statusText = { available:"✓ Available", adopted:"❤️ Adopted", paused:"⏸ Paused" };

  return (
    <div style={styles.catCard}>
      <div style={styles.catCardPhoto}>
        {cat.photos?.[0]
          ? <img src={cat.photos[0]} alt={cat.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          : <span style={{fontSize:32}}>🐱</span>}
      </div>
      <div style={styles.catCardInfo}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
          <div>
            <h3 style={styles.catCardName}>{cat.name}</h3>
            <p style={{fontSize:13,color:"var(--muted)"}}>{cat.age} · {cat.breed || "Mixed"}</p>
          </div>
          <span style={{background:statusColors[cat.status]||"var(--warm)",padding:"4px 12px",borderRadius:50,fontSize:12,fontWeight:600}}>
            {statusText[cat.status]||cat.status}
          </span>
        </div>
        <p style={{fontSize:14,color:"var(--muted)",margin:"8px 0",lineHeight:1.5}}>{cat.shortDesc}</p>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {cat.status !== "adopted" && (
            <>
              <button onClick={()=>onMarkAdopted(cat.id)} style={styles.actionBtn}>
                ❤️ Mark as Adopted
              </button>
              <button onClick={()=>onToggle(cat.id, cat.status)} style={{...styles.actionBtn,background:"var(--warm)",color:"var(--mid)"}}>
                {cat.status === "available" ? "⏸ Pause" : "▶ Resume"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ApplicationCard({ app }) {
  const statusColor = app.status === "pending" ? "#fff3e0" : app.status === "accepted" ? "#e8f5ee" : "#f5e8e8";
  return (
    <div style={{...styles.appCard, background:statusColor}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
        <div>
          <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:18,color:"var(--deep)"}}>
            {app.adopter?.name} wants to adopt {app.catName}
          </h3>
          <p style={{fontSize:13,color:"var(--muted)",marginTop:2}}>
            {app.createdAt?.toDate?.()?.toLocaleDateString?.() || "Recently"}
          </p>
        </div>
        <span style={{fontSize:12,fontWeight:600,textTransform:"uppercase",letterSpacing:1,color:"var(--mid)"}}>
          {app.status}
        </span>
      </div>
      <div style={styles.appDetails}>
        <Detail icon="📍" label="Living" value={app.adopter?.living}/>
        <Detail icon="🐾" label="Experience" value={app.adopter?.experience}/>
        <Detail icon="🏠" label="Household" value={app.adopter?.household}/>
        <Detail icon="💬" label="Why" value={app.adopter?.reason}/>
      </div>
      {app.status === "pending" && (
        <p style={{fontSize:13,color:"var(--muted)",marginTop:10,fontStyle:"italic"}}>
          📱 Check your WhatsApp to accept or reject this application.
        </p>
      )}
    </div>
  );
}

function Detail({ icon, label, value }) {
  if (!value) return null;
  return (
    <div style={{fontSize:14,color:"var(--text)"}}>
      <strong>{icon} {label}:</strong> {value}
    </div>
  );
}

const styles = {
  page: { minHeight:"100vh", padding:"40px 0 80px" },
  container: { maxWidth:900, margin:"0 auto", padding:"0 24px" },

  header: { display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:32 },
  title: { fontFamily:"'Playfair Display',serif", fontSize:36, color:"var(--deep)" },
  sub: { color:"var(--muted)", fontSize:15, marginTop:4 },

  statsRow: { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:32 },
  statCard: { background:"#fff", borderRadius:16, padding:"20px 16px", textAlign:"center", boxShadow:"var(--shadow-s)", display:"flex", flexDirection:"column", alignItems:"center", gap:6 },

  tabs: { display:"flex", gap:4, marginBottom:24, background:"var(--warm)", borderRadius:50, padding:4, width:"fit-content" },
  tab: { padding:"10px 24px", borderRadius:50, border:"none", background:"transparent", color:"var(--muted)", fontSize:15, fontWeight:500, cursor:"pointer", transition:"all 0.2s", fontFamily:"'Outfit',sans-serif" },
  tabActive: { background:"#fff", color:"var(--rust)", boxShadow:"var(--shadow-s)" },

  listGrid: { display:"flex", flexDirection:"column", gap:16 },
  empty: { padding:60, textAlign:"center" },

  catCard: { background:"#fff", borderRadius:16, padding:20, display:"flex", gap:16, boxShadow:"var(--shadow-s)" },
  catCardPhoto: { width:90, height:90, borderRadius:12, overflow:"hidden", background:"var(--warm)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  catCardName: { fontFamily:"'Playfair Display',serif", fontSize:20, color:"var(--deep)" },
  catCardInfo: { flex:1, display:"flex", flexDirection:"column", gap:4 },
  actionBtn: { background:"var(--rust)", color:"#fff", border:"none", padding:"8px 18px", borderRadius:50, fontSize:13, cursor:"pointer", fontFamily:"'Outfit',sans-serif", fontWeight:500 },

  appList: { display:"flex", flexDirection:"column", gap:16 },
  appCard: { borderRadius:16, padding:20, boxShadow:"var(--shadow-s)" },
  appDetails: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 },
};
